import React, { useCallback, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { useShowAlert } from 'hooks';
import API from 'services/API';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import TextBox from 'UI/components/atoms/TextBox';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType } from 'UI/constants/entityTypes';
import { getErrorMessage, REQUIRED_VALIDATION } from 'UI/utils';
import { OptionRenderers, Selectors } from 'UI/utils/renderers';

import { FORM_FIELDS_MAP } from '../../drawers/CreateSendoutDrawer/SendoutForm/SendoutForm.constants';

import { GridContainer } from './CandidateDetailsFields.styled';
import { getCandidateRecruitersOptions } from './CandidateDetailsFields.utils';

const COPIES = strings.sendoutsV2.form.sections.candidateDetails;

export const CandidateDetailsFields = ({ onSelectCandidate, showEstimatedFeeAmount = false }) => {
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [recruiterOptions, setRecruiterOptions] = useState([]);
  const { control, errors, setValue } = useFormContext();
  const { showAlert } = useShowAlert();

  const handleCandidateAutocompleteChange = useCallback(
    async value => {
      setRecruiterOptions([]);
      setValue(FORM_FIELDS_MAP.CandidateRecruiter.key, null, false);

      const candidateId = value?.id;
      if (!candidateId) {
        onSelectCandidate && onSelectCandidate(null);
        return;
      }

      try {
        setIsFetchingDetails(true);

        const candidateResponse = await API.get(`${Endpoints.Candidates}/${candidateId}`, {
          apiVersion: 2
        });

        if (candidateResponse.status !== HTTPStatusCodes.Ok) {
          onSelectCandidate && onSelectCandidate(null);
          return;
        }

        const { data } = candidateResponse;
        onSelectCandidate && onSelectCandidate(data);
        const candidateRecruiters = getCandidateRecruitersOptions(data);
        setRecruiterOptions(candidateRecruiters);

        if (data.additionalRecruiters?.length === 0) {
          const formattedMainRecruiter = {
            ...data.recruiter,
            fullName: data.recruiter?.personalInformation?.full_name
          };

          setValue(FORM_FIELDS_MAP.CandidateRecruiter.key, formattedMainRecruiter);
        }
      } catch (error) {
        showAlert({
          title: strings.shared.errors.serverError.title,
          error: getErrorMessage(error)
        });
      } finally {
        setIsFetchingDetails(false);
      }
    },
    [onSelectCandidate, setValue, showAlert]
  );

  const handleAutocompleteValueChange = useCallback(([, value]) => value, []);
  const handleTextBoxValueChange = useCallback(([, value]) => value, []);
  const handleCandidateChange = useCallback(
    ([, value]) => {
      handleCandidateAutocompleteChange(value);
      return value;
    },
    [handleCandidateAutocompleteChange]
  );

  return (
    <GridContainer container spacing={2}>
      <Grid item xs={12}>
        <Typography color="textSecondary" variant="body2">
          {COPIES.subtitle}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Controller
          name={FORM_FIELDS_MAP.Candidate.key}
          control={control}
          rules={REQUIRED_VALIDATION}
          defaultValue={null}
          as={
            <AutocompleteSelect
              displayKey="fullName"
              error={!!errors[FORM_FIELDS_MAP.Candidate.key]}
              errorText={errors[FORM_FIELDS_MAP.Candidate.key]?.message}
              name={FORM_FIELDS_MAP.Candidate.key}
              placeholder={COPIES.labels.selectCandidate}
              renderOption={OptionRenderers.globalSearchPerson('fullName')}
              getOptionSelected={Selectors.byId}
              typeahead
              typeaheadLimit={50}
              typeaheadParams={{
                schema: 'contactDirectory',
                entityTypeGroup: EntityType.Candidate
              }}
              url={Endpoints.Search}
            />
          }
          onChange={handleCandidateChange}
          onChangeName="onSelect"
          valueName="selectedValue"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Controller
          name={FORM_FIELDS_MAP.CandidateRecruiter.key}
          control={control}
          rules={REQUIRED_VALIDATION}
          defaultValue={null}
          as={
            <AutocompleteSelect
              disabled={recruiterOptions.length === 0}
              displayKey="fullName"
              error={!!errors[FORM_FIELDS_MAP.CandidateRecruiter.key]}
              errorText={errors[FORM_FIELDS_MAP.CandidateRecruiter.key]?.message}
              fetching={isFetchingDetails}
              name={FORM_FIELDS_MAP.CandidateRecruiter.key}
              options={recruiterOptions}
              placeholder={COPIES.labels.candidateRecruiter}
            />
          }
          onChange={handleAutocompleteValueChange}
          onChangeName="onSelect"
          valueName="selectedValue"
        />
      </Grid>

      <When condition={showEstimatedFeeAmount}>
        <Grid item xs={12}>
          <Controller
            name={FORM_FIELDS_MAP.EstimatedFeeAmount.key}
            control={control}
            rules={REQUIRED_VALIDATION}
            defaultValue=""
            as={
              <TextBox
                error={!!errors[FORM_FIELDS_MAP.EstimatedFeeAmount.key]}
                errorText={errors[FORM_FIELDS_MAP.EstimatedFeeAmount.key]?.message}
                name={FORM_FIELDS_MAP.EstimatedFeeAmount.key}
                label={COPIES.labels.estimatedFeeAmount}
                inputType="currency"
                placeholder={COPIES.labels.estimatedFeeAmount}
              />
            }
            onChange={handleTextBoxValueChange}
            onChangeName="onChange"
            valueName="value"
          />
        </Grid>
      </When>
    </GridContainer>
  );
};
